import React, { useEffect, useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  TextField,
  Box,
  Divider,
  MenuItem,
  Grid2,
  FormControl,
  InputLabel,
  Select,
  Typography,
  IconButton,
  Button,
  OutlinedInput,
  Alert,
  FormHelperText,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import FormDialog from "@components/FormDialog";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "@context/SnackBarProvider";
import { useMutation, useQuery } from "@tanstack/react-query";

import { AxiosError } from "axios";
import { Driver } from "@lib/types/driver";
import useHandleError from "@lib/utils/errorHook";
import { getDrivers } from "@lib/api/queries/drivers";

import Autocomplete from "@mui/material/Autocomplete";
import { Unit } from "@lib/types/units";
import { getUnits } from "@lib/api/queries/units";
import {
  BindingFormData,
  bindingSchema,
  createBinding,
} from "@lib/api/mutations/binding";
import { BindingResponse, RecurringType } from "@lib/types/binding";
import { BindingTypeOptions, weekdays } from "@lib/constants/general";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { Plus, Trash2, X } from "lucide-react";
import { getDaysLabel } from "@lib/utils/string";
import { getUnitGroups } from "@lib/api/queries/unit_groups";
import { UnitGroupsList } from "@lib/types/unit_group";
import { getDriverGroups } from "@lib/api/queries/driver_groups";
import { DriverGroupsList } from "@lib/types/driver_group";

interface Props {
  createBindingDialogOpen: boolean;
  setCreateBindingDialogOpen: (open: boolean) => void;
  refetch?: () => void;
}

const CreateBinding: React.FC<Props> = ({
  createBindingDialogOpen,
  setCreateBindingDialogOpen,
  refetch,
}) => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const handleError = useHandleError();

  const [duration, setDuration] = useState(1);
  const [durationUnit, setDurationUnit] = useState("hours");

  const [driverType, setDriverType] = useState("driver");
  const [unitType, setUnitType] = useState("unit");

  const {
    data: drivers,
    isFetching,
    error,
  } = useQuery<Driver[]>({
    queryKey: ["drivers"],
    queryFn: async () => {
      const drivers = await getDrivers();

      const filteredDrivers = drivers?.data.filter((driver) => !!driver.id);
      return filteredDrivers;
    },
    enabled: createBindingDialogOpen,
    refetchOnWindowFocus: false,
  });

  const { data: units, error: unitsError } = useQuery<Unit[]>({
    queryKey: ["units"],
    queryFn: async () => {
      const units = await getUnits();

      return units?.units;
    },
    enabled: createBindingDialogOpen,
    refetchOnWindowFocus: false,
  });

  const { data: unitGroups, error: unitGroupError } = useQuery<UnitGroupsList>({
    queryKey: ["unit-groups"],
    queryFn: async () => {
      return getUnitGroups();
    },
    enabled: createBindingDialogOpen,
    refetchOnWindowFocus: false,
  });

  const { data: driverGroups, error: driverGroupError } =
    useQuery<DriverGroupsList>({
      queryKey: ["driver-groups"],
      queryFn: async () => {
        return getDriverGroups();
      },
      enabled: createBindingDialogOpen,
      refetchOnWindowFocus: false,
    });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm<BindingFormData>({
    resolver: zodResolver(bindingSchema),
    defaultValues: {
      binding_type: "",
      driver_id: undefined,
      driver_group_id: undefined,
      unit_id: undefined,
      unit_group_id: undefined,
    },
  });

  const bindingType = watch("binding_type");
  const schedules = watch("schedules");

  // Field Array for dynamic custom fields
  const { fields, append, remove } = useFieldArray({
    control,
    name: "schedules",
  });

  const { mutate, isPending: isCreatingBinding } = useMutation({
    mutationFn: createBinding,
    onSuccess: (data: BindingResponse) => {
      showSnackbar(t("user.bindings.addSuccess"), "success");

      setCreateBindingDialogOpen(false);
      reset();
      refetch && refetch();
    },
    onError: (error: AxiosError | unknown) => {
      if (error instanceof AxiosError && error.response) {
        const errorDetails = handleError(error.response.data);
        errorDetails && showSnackbar(errorDetails.message, "error");
      } else {
        showSnackbar(t("general.error"), "error");
      }
    },
  });

  const handleAddBindings = (data: BindingFormData) => {
    mutate(data);
  };

  const handleDateChange = (newValue: Date | null, field: any) => {
    if (newValue) {
      const minutes = newValue.getMinutes();

      // Round minutes to the nearest multiple of 5
      const roundedMinutes = Math.round(minutes / 5) * 5;
      newValue.setMinutes(roundedMinutes); // Set the minutes to the rounded value
    }
    field.onChange(newValue || null); // Pass the new value to react-hook-form
  };

  const calculateDateRange = () => {
    const now = new Date();
    const end = new Date(now);
    if (durationUnit === "hours") {
      end.setHours(now.getHours() + duration);
    } else if (durationUnit === "days") {
      end.setDate(now.getDate() + duration);
    }

    setValue("start_datetime", now);
    setValue("end_datetime", end);
  };

  useEffect(() => {
    if (bindingType === "expiring") {
      calculateDateRange();
    } else {
      setValue("start_datetime", undefined);
      setValue("end_datetime", undefined);
    }
  }, [bindingType, setValue]);

  const handleDriverTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("driver_id", undefined);
    setValue("driver_group_id", undefined);
    setDriverType(e.target.value);
  };

  const handleUnitTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("unit_id", undefined);
    setValue("unit_group_id", undefined);
    setUnitType(e.target.value);
  };

  return (
    <FormDialog
      title={t("user.bindings.add")}
      open={createBindingDialogOpen}
      onClose={() => {
        setCreateBindingDialogOpen(false);
        reset();
      }}
      onSubmit={handleSubmit(handleAddBindings)}
      submitText={t("general.save")}
      isSubmitting={isCreatingBinding}
      submittingText={t("general.saving")}
    >
      {!isFetching && (
        <form noValidate>
          <Box display="flex" flexDirection="column" gap={2} py={2}>
            <Controller
              name="binding_type"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label={t("user.bindings.bindingType")}
                  placeholder={t("user.bindings.selectBindingType")}
                  variant="outlined"
                  fullWidth
                  error={!!errors.binding_type}
                  helperText={errors.binding_type?.message}
                >
                  {BindingTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {t(`user.bindings.${option.label}`)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <Box
              display="flex"
              flexDirection="column"
              padding={1}
              sx={{
                bgcolor: "#eeeeee",
                borderRadius: 3,
              }}
            >
              <RadioGroup
                row
                value={driverType}
                onChange={handleDriverTypeChange}
              >
                <FormControlLabel
                  value="driver"
                  control={<Radio color="primary" />}
                  label="Individual Driver"
                />
                <FormControlLabel
                  value="driver_group"
                  control={<Radio color="primary" />}
                  label="Driver Group"
                />
              </RadioGroup>

              {driverType === "driver_group" && (
                <Controller
                  name="driver_group_id"
                  control={control}
                  render={({ field }) => (
                    <div>
                      <Autocomplete
                        {...field}
                        options={driverGroups?.data || []}
                        value={
                          driverGroups?.data.find(
                            (driverGroup) =>
                              driverGroup.group_id === field.value
                          ) || null
                        }
                        onChange={(_, value) =>
                          field.onChange(value ? value.group_id : null)
                        }
                        loading={isFetching}
                        getOptionLabel={(option) =>
                          option && typeof option === "object"
                            ? `${option.name}`
                            : ""
                        }
                        renderOption={(props, option) => (
                          <li
                            {...props}
                            key={`${option.name}-${option.group_id}`}
                          >
                            {option.name}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("user.bindings.driverGroup")}
                            placeholder={t("user.bindings.selectDriverGroup")}
                            variant="outlined"
                            error={!!errors.unit_id}
                            helperText={errors.unit_id?.message}
                          />
                        )}
                      />
                    </div>
                  )}
                />
              )}

              {driverType === "driver" && (
                <Controller
                  name="driver_id"
                  control={control}
                  render={({ field }) => (
                    <div>
                      <Autocomplete
                        {...field}
                        options={drivers || []}
                        value={
                          drivers?.find(
                            (driver) => driver.id === field.value
                          ) || null
                        }
                        onChange={(_, value) =>
                          field.onChange(value ? value.id : null)
                        }
                        loading={isFetching}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        getOptionLabel={(option) => option.name || ""}
                        renderOption={(props, option) => (
                          <li {...props} key={`${option.name}-${option.id}`}>
                            {option.name}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("user.bindings.driver")}
                            placeholder={t("user.bindings.selectDriver")}
                            variant="outlined"
                            error={!!errors.driver_id}
                            helperText={errors.driver_id?.message}
                          />
                        )}
                      />
                    </div>
                  )}
                />
              )}
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              padding={1}
              sx={{
                bgcolor: "#eeeeee",
                borderRadius: 3,
              }}
            >
              <RadioGroup row value={unitType} onChange={handleUnitTypeChange}>
                <FormControlLabel
                  value="unit"
                  control={<Radio color="primary" />}
                  label="Individual Unit"
                />
                <FormControlLabel
                  value="unit_group"
                  control={<Radio color="primary" />}
                  label="Unit Group"
                />
              </RadioGroup>

              {unitType === "unit_group" && (
                <Controller
                  name="unit_group_id"
                  control={control}
                  render={({ field }) => (
                    <div>
                      <Autocomplete
                        {...field}
                        options={unitGroups?.data || []}
                        value={
                          unitGroups?.data.find(
                            (unitGroup) => unitGroup.group_id === field.value
                          ) || null
                        }
                        onChange={(_, value) =>
                          field.onChange(value ? value.group_id : null)
                        }
                        loading={isFetching}
                        getOptionLabel={(option) =>
                          option && typeof option === "object"
                            ? `${option.name}`
                            : ""
                        }
                        renderOption={(props, option) => (
                          <li
                            {...props}
                            key={`${option.name}-${option.group_id}`}
                          >
                            {option.name}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("user.bindings.unitGroup")}
                            placeholder={t("user.bindings.selectUnitGroup")}
                            variant="outlined"
                            error={!!errors.unit_id}
                            helperText={errors.unit_id?.message}
                          />
                        )}
                      />
                    </div>
                  )}
                />
              )}
              {unitType === "unit" && (
                <Controller
                  name="unit_id"
                  control={control}
                  render={({ field }) => (
                    <div>
                      <Autocomplete
                        {...field}
                        options={units || []}
                        value={
                          units?.find(
                            (units) => units.unit_id === field.value
                          ) || null
                        }
                        onChange={(_, value) =>
                          field.onChange(value ? value.unit_id : null)
                        }
                        loading={isFetching}
                        getOptionLabel={(option) =>
                          option && typeof option === "object"
                            ? `${option.name}`
                            : ""
                        }
                        renderOption={(props, option) => (
                          <li
                            {...props}
                            key={`${option.name}-${option.unit_id}`}
                          >
                            {option.name}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("user.bindings.unit")}
                            placeholder={t("user.bindings.selectUnit")}
                            variant="outlined"
                            error={!!errors.unit_id}
                            helperText={errors.unit_id?.message}
                          />
                        )}
                      />
                    </div>
                  )}
                />
              )}
            </Box>
            <Divider />

            <Grid2 container spacing={2} columns={{ xs: 1, md: 6 }}>
              {bindingType === "expiring" ? (
                <>
                  <Grid2 size={3}>
                    <TextField
                      label={t("user.bindings.duration")}
                      type="number"
                      value={duration}
                      onChange={(e) => {
                        setDuration(Number(e.target.value));

                        if (bindingType === "expiring") {
                          calculateDateRange();
                        }
                      }}
                      fullWidth
                      error={!!errors.start_datetime || !!errors.end_datetime}
                      helperText={errors.start_datetime?.message}
                    />
                  </Grid2>
                  <Grid2 size={3}>
                    <FormControl fullWidth>
                      <InputLabel>{t("user.bindings.interval")}</InputLabel>
                      <Select
                        label={t("user.bindings.interval")}
                        value={durationUnit}
                        onChange={(e) => {
                          setDurationUnit(e.target.value);

                          if (bindingType === "expiring") {
                            calculateDateRange();
                          }
                        }}
                      >
                        <MenuItem value="hours">
                          {t("user.bindings.hours")}
                        </MenuItem>
                        <MenuItem value="days">
                          {t("user.bindings.days")}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid2>
                </>
              ) : (
                <>
                  <Grid2 size={3}>
                    <Controller
                      name="start_datetime"
                      control={control}
                      render={({ field }) => (
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DateTimePicker
                            {...field}
                            label={t("user.bindings.startDatetime")}
                            value={field.value || null} // Ensure null for no value
                            onChange={(newValue) =>
                              handleDateChange(newValue, field)
                            }
                            minDateTime={new Date()} // Ensure the date is not in the past
                            slotProps={{
                              textField: {
                                variant: "outlined",
                                error: !!errors.start_datetime,
                                helperText: errors.start_datetime?.message,
                              },
                              actionBar: {
                                actions: ["clear", "accept"],
                              },
                              field: {
                                clearable: true,
                              },
                            }}
                            sx={{ width: "100%" }}
                          />
                        </LocalizationProvider>
                      )}
                    />
                  </Grid2>
                  <Grid2 size={3}>
                    <Controller
                      name="end_datetime"
                      control={control}
                      render={({ field }) => (
                        <DateTimePicker
                          {...field}
                          label={t("user.bindings.endDatetime")}
                          value={field.value || null} // Ensure null for no value
                          onChange={(newValue) =>
                            handleDateChange(newValue, field)
                          }
                          minDateTime={new Date()} // Ensure the date is not in the past
                          slotProps={{
                            textField: {
                              variant: "outlined",
                              error: !!errors.end_datetime,
                              helperText: errors.end_datetime?.message,
                            },
                            actionBar: {
                              actions: ["clear", "accept"],
                            },
                            field: {
                              clearable: true,
                            },
                          }}
                          sx={{ width: "100%" }}
                        />
                      )}
                    />
                  </Grid2>
                </>
              )}
            </Grid2>

            {bindingType === "recurring" && (
              <>
                <Divider />
                {/* Schedules Section */}
                <Box gap={3}>
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                    {t("user.bindings.schedules")}
                  </Typography>
                  {fields.map((field, index) => (
                    <Grid2
                      container
                      spacing={2}
                      columns={{ xs: 1, md: 6 }}
                      gap={2}
                      sx={{ mb: 2 }}
                    >
                      <Grid2 size={6}>
                        <Box
                          display="flex"
                          flexDirection="row"
                          alignItems="center"
                          gap={1}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "bold" }}
                          >
                            {t("user.bindings.schedule")} {index + 1}
                          </Typography>
                          <Divider
                            sx={{ flexGrow: 1, marginY: 0 }}
                            // variant="middle"
                          />
                          <IconButton
                            onClick={() => remove(index)}
                            color="error"
                            size="small"
                          >
                            <Trash2 size={16} />
                            <Typography variant="body2">
                              {t("user.bindings.remove")}
                            </Typography>
                          </IconButton>
                        </Box>
                      </Grid2>

                      <Grid2 size={3}>
                        <Controller
                          name={`schedules.${index}.recurring_type`}
                          control={control}
                          render={({ field }) => (
                            <FormControl sx={{ width: "100%" }}>
                              <InputLabel id={`recurring-type-label-${index}`}>
                                {t("user.bindings.recurringType")}
                              </InputLabel>

                              <Select
                                labelId={`recurring-type-label-${index}`}
                                id={`recurring-type-${index}`}
                                value={field.value}
                                onChange={(e) => {
                                  const newRecurringType = e.target.value;
                                  field.onChange(newRecurringType);

                                  setValue(
                                    `schedules.${index}.days_of_week`,
                                    undefined
                                  );
                                  setValue(
                                    `schedules.${index}.day_of_month`,
                                    undefined
                                  );
                                }}
                                input={
                                  <OutlinedInput
                                    id={`recurring-type-input-${index}`}
                                    label={t("user.bindings.recurringType")}
                                  />
                                }
                                renderValue={(selected) => (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Typography variant="body2">
                                      {selected === RecurringType.WEEKLY
                                        ? t("Weekly")
                                        : t("Monthly")}
                                    </Typography>
                                  </Box>
                                )}
                              >
                                <MenuItem value="WEEKLY">
                                  {t("Weekly")}
                                </MenuItem>
                                <MenuItem value="MONTHLY">
                                  {t("Monthly")}
                                </MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        />
                      </Grid2>

                      <Grid2 size={3}>
                        {schedules?.[index]?.recurring_type ===
                          RecurringType.WEEKLY && (
                          <Controller
                            name={`schedules.${index}.days_of_week`}
                            control={control}
                            render={({ field }) => (
                              <FormControl
                                sx={{ width: "100%" }}
                                error={
                                  !!errors?.schedules?.[index]?.days_of_week
                                }
                              >
                                <InputLabel id={`days-of-week-label-${index}`}>
                                  {t("user.bindings.daysOfWeek")}
                                </InputLabel>
                                <Select
                                  {...field} // Connect to React Hook Form
                                  labelId={`days-of-week-label-${index}`}
                                  id={`days-of-week-${index}`}
                                  multiple
                                  value={
                                    Array.isArray(field.value)
                                      ? field.value
                                      : []
                                  } // Ensure the value is always an array
                                  onChange={(event) => {
                                    const selectedValues = event.target
                                      .value as number[];
                                    field.onChange(selectedValues);
                                  }}
                                  input={
                                    <OutlinedInput
                                      id={`days-of-week-input-${index}`}
                                      label="Days of Week"
                                    />
                                  }
                                  renderValue={(selected) => (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                      }}
                                    >
                                      <Typography variant="body2">
                                        {getDaysLabel(selected)}
                                      </Typography>
                                    </Box>
                                  )}
                                >
                                  {Array.from({ length: 7 }, (_, i) => i).map(
                                    (day) => (
                                      <MenuItem key={day} value={day}>
                                        {weekdays[day]}
                                      </MenuItem>
                                    )
                                  )}
                                </Select>
                                <FormHelperText>
                                  {
                                    errors?.schedules?.[index]?.days_of_week
                                      ?.message
                                  }
                                </FormHelperText>
                              </FormControl>
                            )}
                          />
                        )}
                        {schedules?.[index]?.recurring_type ===
                          RecurringType.MONTHLY && (
                          <>
                            <Controller
                              name={`schedules.${index}.day_of_month`}
                              control={control}
                              render={({ field }) => (
                                <FormControl sx={{ width: "100%" }}>
                                  <InputLabel
                                    id={`day-of-month-label-${index}`}
                                  >
                                    {t("user.bindings.daysOfMonth")}
                                  </InputLabel>
                                  <Select
                                    {...field} // Connect to React Hook Form
                                    labelId={`day-of-month-label-${index}`}
                                    id={`days-of-month-${index}`}
                                    value={field.value || ""} // Ensure value is defined
                                    onChange={(event) => {
                                      const selectedValue = event.target
                                        .value as number;
                                      field.onChange(selectedValue); // Update the value
                                    }}
                                    input={
                                      <OutlinedInput
                                        id={`day-of-month-input-${index}`}
                                        label={t("user.bindings.daysOfMonth")}
                                      />
                                    }
                                    renderValue={(selected) => (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexWrap: "wrap",
                                          gap: 0.5,
                                        }}
                                      >
                                        <Typography variant="body2">
                                          {selected}
                                        </Typography>
                                      </Box>
                                    )}
                                  >
                                    {Array.from(
                                      { length: 31 },
                                      (_, i) => i + 1
                                    ).map((day) => (
                                      <MenuItem key={day} value={day}>
                                        {day}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              )}
                            />
                          </>
                        )}
                      </Grid2>

                      <Grid2 size={3}>
                        <Controller
                          name={`schedules.${index}.start_time`}
                          control={control}
                          render={({ field }) => (
                            <TimePicker
                              label={t("user.bindings.startTime")}
                              value={
                                field.value
                                  ? new Date(`1970-01-01T${field.value}`)
                                  : null
                              }
                              onChange={(value) => {
                                if (value) {
                                  const hours = String(
                                    value.getHours()
                                  ).padStart(2, "0");
                                  const minutes = String(
                                    value.getMinutes()
                                  ).padStart(2, "0");
                                  const seconds = String(
                                    value.getSeconds()
                                  ).padStart(2, "0");
                                  const timeString = `${hours}:${minutes}:${seconds}`; // Format to hh:mm:ss
                                  field.onChange(timeString); // Update with string value
                                } else {
                                  field.onChange(""); // Handle clearing the value
                                }
                              }}
                              slotProps={{
                                textField: {
                                  variant: "outlined",
                                  error:
                                    !!errors?.schedules?.[index]?.start_time,
                                  helperText:
                                    errors?.schedules?.[index]?.start_time
                                      ?.message,
                                  sx: {
                                    width: "100%",
                                  },
                                },
                              }}
                              ampm={false}
                            />
                          )}
                        />
                      </Grid2>

                      <Grid2 size={3}>
                        <Controller
                          name={`schedules.${index}.end_time`}
                          control={control}
                          render={({ field }) => (
                            <TimePicker
                              label={t("user.bindings.endTime")}
                              value={
                                field.value
                                  ? new Date(`1970-01-01T${field.value}`)
                                  : null
                              }
                              onChange={(value) => {
                                if (value) {
                                  const hours = String(
                                    value.getHours()
                                  ).padStart(2, "0");
                                  const minutes = String(
                                    value.getMinutes()
                                  ).padStart(2, "0");
                                  const seconds = String(
                                    value.getSeconds()
                                  ).padStart(2, "0");
                                  const timeString = `${hours}:${minutes}:${seconds}`; // Format to hh:mm:ss
                                  field.onChange(timeString); // Update with string value
                                } else {
                                  field.onChange(""); // Handle clearing the value
                                }
                              }}
                              ampm={false}
                              slotProps={{
                                textField: {
                                  variant: "outlined",
                                  error: !!errors?.schedules?.[index]?.end_time,
                                  helperText:
                                    errors?.schedules?.[index]?.end_time
                                      ?.message,
                                  sx: {
                                    width: "100%",
                                  },
                                },
                              }}
                            />
                          )}
                        />
                      </Grid2>
                    </Grid2>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<Plus />}
                    onClick={() =>
                      append({
                        day_of_month: undefined,
                        days_of_week: undefined,
                        start_time: "",
                        end_time: "",
                        recurring_type: "weekly",
                      })
                    }
                  >
                    {t("user.bindings.addSchedule")}
                  </Button>

                  {errors.schedules?.message && (
                    <Alert
                      severity="error"
                      sx={{
                        mt: 2,
                        mb: 2,
                      }}
                    >
                      {errors.schedules.message}
                    </Alert>
                  )}
                </Box>
              </>
            )}
          </Box>
        </form>
      )}
    </FormDialog>
  );
};

export default CreateBinding;
